// Малые окна формул: экспликация «где», вставка формулы из любого формата, справка по вводу.
import { useMemo, useState } from 'react';
import type { DialogProps } from '../../core/registry';
import type { WhereItem } from '../../core/schema/math';
import { Btn, Modal } from '../../ui/controls';
import { WhereEditor } from './where';
import { renderTex } from './ml';
import { anyToLatex, detectFormat, FORMAT_NAMES, type MathFormat } from './detect';
import { knownSymbols } from './state';
import { symbols } from './latex';

export function WhereDialog({ ctx, props, close }: DialogProps<WhereItem[]>) {
  const known = useMemo(() => knownSymbols(ctx.editor, props.pos ?? undefined), []);
  const init: WhereItem[] = useMemo(() => {
    const w: WhereItem[] = props.where || [];
    if (w.length || !props.autofill) return w;
    return symbols(props.latex || '').map((s) => ({ sym: s, desc: known.get(s)?.desc || '' }));
  }, []);
  const [where, setWhere] = useState<WhereItem[]>(init);
  return (
    <Modal title="Экспликация «где …»" onClose={() => close()} width={860} testid="where-dialog" footer={<>
      <Btn label="Отмена" onClick={() => close()} />
      <Btn label="Готово" primary onClick={() => close(where.filter((w) => w.sym.trim() || String(w.desc || '').trim()))} testid="where-ok" />
    </>}>
      <div className="mx-where-formula" dangerouslySetInnerHTML={{ __html: renderTex(props.latex || '', true) }} />
      <WhereEditor latex={props.latex || ''} value={where} onChange={setWhere} known={known} />
    </Modal>
  );
}

export interface ImportResult { latex: string; mode: 'num' | 'calc' | 'inline' }

const FORMATS: (MathFormat | 'auto')[] = ['auto', 'latex', 'linear', 'asciimath', 'mathml', 'omml', 'chem'];

/** Вставка формулы из текста любого формата с распознаванием и предпросмотром. */
export function ImportDialog({ props, close }: DialogProps<ImportResult>) {
  const [text, setText] = useState<string>(props.text || '');
  const [fmt, setFmt] = useState<MathFormat | 'auto'>('auto');
  const det = useMemo(() => (text.trim() ? detectFormat(text) : 'text'), [text]);
  const res = useMemo(() => { try { return text.trim() ? anyToLatex(text, fmt === 'auto' ? undefined : fmt) : null; } catch { return null; } }, [text, fmt]);
  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const html = e.clipboardData.getData('text/html');
    if (/msEquation|<m:oMath|<math[\s>]/i.test(html)) { e.preventDefault(); setText(html); }
  };
  const done = (mode: ImportResult['mode']) => res?.latex && close({ latex: res.latex, mode });
  return (
    <Modal title="Вставить формулу из LaTeX, Word, MathML или текста" onClose={() => close()} width={820} testid="math-import" footer={<>
      <span className="mx-grow" />
      <Btn label="Отмена" onClick={() => close()} />
      <Btn label="В строке" onClick={() => done('inline')} disabled={!res?.latex} testid="import-inline" />
      <Btn label="Без номера" onClick={() => done('calc')} disabled={!res?.latex} />
      <Btn label="С номером" primary onClick={() => done(res?.display === false ? 'inline' : 'num')} disabled={!res?.latex} testid="import-ok" />
    </>}>
      <div className="mx-import">
        <textarea className="mono" rows={6} value={text} autoFocus spellCheck={false} onChange={(e) => setText(e.target.value)} onPaste={onPaste}
          placeholder={'Вставьте формулу (Ctrl+V): из Word, с сайта (MathML), LaTeX ($…$, \\[…\\], \\begin{equation}),\nлинейную запись Word (x^2/(y+1), √(x), ∑_(i=1)^n▒x_i), AsciiMath (sqrt(x), sum_(i=1)^n),\nтекст «x² + y² = r²» или химическую запись «2H2 + O2 -> 2H2O»'}
          data-testid="import-text" />
        <div className="mx-import-fmt">
          <span className="muted">Формат:</span>
          {FORMATS.map((f) => (
            <button key={f} type="button" className={'mx-chip' + (fmt === f ? ' is-on' : '')} onClick={() => setFmt(f)}>
              {f === 'auto' ? 'авто' + (text.trim() ? ` (${FORMAT_NAMES[det]})` : '') : FORMAT_NAMES[f]}</button>
          ))}
        </div>
        <div className="mx-import-prev" data-testid="import-preview">
          {res?.latex ? <span dangerouslySetInnerHTML={{ __html: renderTex(res.latex, true) }} /> : <span className="muted">Здесь появится формула</span>}
        </div>
        {res?.latex && <div className="mono mx-small muted" data-testid="import-latex">{res.latex}</div>}
      </div>
    </Modal>
  );
}

const HELP: [string, string][] = [
  ['Alt+=', 'новая формула: в пустом абзаце - отдельной строкой с номером, в тексте - в строке; с выделенным текстом - превратить его в формулу'],
  ['$…$', 'в тексте: «$x^2$» сразу становится формулой в строке'],
  ['$$ + пробел', 'в пустом абзаце - формула отдельной строкой'],
  ['/', 'дробь: «a/b» → числитель и знаменатель'],
  ['^  _', 'степень и индекс; стрелка вправо - выйти из них'],
  ['sqrt, sum, int, lim', 'корень, сумма, интеграл, предел (набирайте слово)'],
  ['alpha, beta, pi, omega…', 'греческие буквы (в отчёте - прямым шрифтом, 4.6.1.10)'],
  ['sin, cos, tg, ln, lg…', 'функции - прямым шрифтом автоматически'],
  ['* или xx', 'знак умножения «·» или «×» (4.6.1.5)'],
  ['<=  >=  !=  ~=  ->  =>', '≤ ≥ ≠ ≈ → ⇒'],
  ['3.14', 'десятичная точка сама станет запятой: 3,14 (4.6.2.11)'],
  ['Tab', 'к следующему пустому месту шаблона'],
  ['Enter', 'закончить формулу, курсор - после неё'],
  ['Esc', 'закончить формулу и выделить её'],
  ['Enter / F2 на выделенной формуле', 'изменить её'],
  ['Двойной щелчок', 'окно редактора: палитры, код LaTeX / линейная запись Word, экспликация «где», перенос'],
  ['Ctrl+V', 'формула из Word, с сайта (MathML), LaTeX или линейная запись - распознаётся сама'],
];

export function HelpDialog({ close }: DialogProps) {
  return (
    <Modal title="Как набирать формулы" onClose={() => close()} width={760} testid="math-help" footer={<Btn label="Понятно" primary onClick={() => close()} />}>
      <table className="mx-help">
        <tbody>{HELP.map(([k, v]) => <tr key={k}><td><kbd>{k}</kbd></td><td>{v}</td></tr>)}</tbody>
      </table>
      <p className="muted mx-small">Оформление по СТО делается при экспорте само: формулы по левому краю с отступом 1,25 см, номер «(N)» у правого поля,
        в приложениях «(А.1)», запятая перед «где» и между формулами подряд, экспликация с «–» и «;», минус «−», шрифт Cambria Math.</p>
    </Modal>
  );
}
